import $ from "jquery"
import "bootstrap"
import {
  saveLeadCaptureAnswers,
  storedLeadCaptureAnswersForForm,
  leadCaptureCanSkip,
  leadCaptureAnswersToWrite
} from "./lead_capture_session"

export { saveLeadCaptureAnswers }

const DEFAULT_MODAL_ID = "leadCaptureEmailModal"

function modalSelector(modalId) {
  return `#${modalId || DEFAULT_MODAL_ID}`
}

export function parseLeadCaptureFromForm(form) {
  if (!form || !form.dataset.leadCaptureConfig) return null
  try {
    return JSON.parse(form.dataset.leadCaptureConfig)
  } catch (_e) {
    return null
  }
}

export function signUpEmailAlreadyCaptured(form) {
  const el = form.querySelector('input[name="accounts_sign_up[email]"]')
  if (!el) return false
  const v = el.value.trim()
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)
}

/** Tenant JSON / portal forms often store booleans as "1" / "0" (Rails check_box). */
function tenantConfigBool(val) {
  if (val === true || val === 1) return true
  if (val === false || val === 0 || val == null) return false
  if (typeof val === "string") {
    const s = val.trim().toLowerCase()
    return s === "1" || s === "true" || s === "t" || s === "on"
  }
  return false
}

/**
 * True when the how-heard dropdown should appear inside the modal for this status,
 * given the LeadCaptureModalConfig.client_payload[:howHeard] block.
 */
export function howHeardEnabledForStatus(full, addressStatus) {
  const cfg = (full && full.howHeard) || null
  if (!cfg || !tenantConfigBool(cfg.enabled)) return false
  if (cfg.byStatus && addressStatus) {
    const entry = cfg.byStatus[addressStatus]
    if (entry && entry.enabled !== undefined) return tenantConfigBool(entry.enabled)
  }
  return true
}

function leadCaptureFormScope(form) {
  if (form && form.dataset && form.dataset.leadCaptureFormScope) {
    return form.dataset.leadCaptureFormScope
  }
  if (form && form.querySelector('[name^="status["]')) return "status"
  return "accounts_sign_up"
}

function emailCapturedInForm(form, formScope) {
  if (!form) return false
  const el = form.querySelector(`input[name="${formScope}[email]"]`)
  if (!el) return false
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(el.value.trim())
}

function howHeardCapturedInForm(form, formScope) {
  if (!form) return false
  const scope = formScope || "accounts_sign_up"
  const el = form.querySelector(`input[name="${scope}[how_heard]"], select[name="${scope}[how_heard]"]`)
  return !!(el && String(el.value || "").trim())
}

/** True when we should not open the lead-capture email modal (proceed with runNext / submit). */
export function shouldSkipLeadCaptureModal(form, full, addressStatus, currentStep, _addressId) {
  if (!full || !shouldInterceptLeadCapture(full, addressStatus, currentStep)) return true

  const formScope = leadCaptureFormScope(form)
  return leadCaptureCanSkip({
    emailInForm: signUpEmailAlreadyCaptured(form) || emailCapturedInForm(form, formScope),
    howHeardInForm: howHeardCapturedInForm(form, formScope),
    needsHowHeard: howHeardEnabledForStatus(full, addressStatus),
    signupId: signupIdFromForm(form)
  })
}

function signupIdFromForm(form) {
  if (!form || !form.dataset) return ""
  return form.dataset.leadCaptureSignupId || ""
}

function formFieldValue(form, name) {
  const fields = form.querySelectorAll(`[name="${name}"]`)
  for (let i = 0; i < fields.length; i++) {
    const current = String(fields[i].value || "").trim()
    if (current) return current
  }
  return ""
}

export function bindStoredLeadCaptureToForm(form) {
  if (!form) return
  try {
    storedLeadCaptureAnswersForForm(signupIdFromForm(form))
  } catch (_e) {
    // Binding cached answers must not stop the page.
  }
}

function writeLeadCaptureField(form, scope, field, value, overwrite) {
  const name = `${scope}[${field}]`
  const fields = Array.from(form.querySelectorAll(`[name="${name}"]`))
  let keptExisting = false

  fields.forEach((el) => {
    const current = String(el.value || "").trim()
    if (current && !overwrite) {
      keptExisting = true
      return
    }
    if (el.tagName === "SELECT") {
      const hasOption = Array.from(el.options || []).some((option) => option.value === value)
      if (!hasOption) {
        const option = document.createElement("option")
        option.value = value
        option.textContent = value
        el.appendChild(option)
      }
      el.value = value
      keptExisting = true
      return
    }
    el.value = value
    keptExisting = true
  })

  if (!keptExisting) {
    const hidden = document.createElement("input")
    hidden.type = "hidden"
    hidden.name = name
    hidden.value = value
    form.appendChild(hidden)
  }
}

/** Copy session answers onto the form so a skipped modal still posts them. */
export function applyStoredLeadCaptureAnswersToForm(form) {
  if (!form) return
  try {
    const scope = leadCaptureFormScope(form)
    const answers = storedLeadCaptureAnswersForForm(signupIdFromForm(form))
    const decision = leadCaptureAnswersToWrite(answers, {
      email: formFieldValue(form, `${scope}[email]`),
      how_heard: formFieldValue(form, `${scope}[how_heard]`)
    })

    if (decision.email) writeLeadCaptureField(form, scope, "email", decision.email, false)
    if (decision.writeHowHeard) {
      writeLeadCaptureField(form, scope, "how_heard", decision.how_heard, false)
      writeLeadCaptureField(form, scope, "how_heard_other", decision.how_heard_other || "", true)
    }
  } catch (_e) {
    // Applying cached answers must not stop submission.
  }
}

export function prefillLeadCaptureModalFromSession($modal, form) {
  if (!$modal || !$modal.length) return
  const answers = storedLeadCaptureAnswersForForm(signupIdFromForm(form))
  const $email = $modal.find(".lead-capture-modal-email")
  if (answers.email && !$email.val()) $email.val(answers.email)

  if (!answers.how_heard) return
  const $select = $modal.find(".lead-capture-modal-how-heard")
  if ($select.length && !$select.val()) {
    $select.val(answers.how_heard)
    $select.trigger("change")
  }
  if (answers.how_heard_other) {
    const $other = $modal.find(".lead-capture-modal-how-heard-other")
    if ($other.length && !$other.val()) $other.val(answers.how_heard_other)
  }
}

export function shouldInterceptLeadCapture(full, addressStatus, currentStep) {
  if (!full || !tenantConfigBool(full.masterEnabled)) return false
  if (full.legacyEnabled && currentStep === "service_address") return true
  const entry = (full.byStatus || {})[addressStatus]
  if (!entry) return false
  const enabled = tenantConfigBool(entry.enabled)
  const screen = entry.screen || "service_address"
  return enabled && screen === currentStep
}

export function mergeLeadCaptureCopy(full, addressStatus) {
  const defaults = full.defaultCopy || {}
  const fromContent = (full.copyByStatus || {})[addressStatus] || {}
  return { ...defaults, ...fromContent }
}

export function leadCaptureDismissable(addressId, addressStatus, currentStep) {
  if (currentStep !== "service_address") return true
  return !(addressId && addressStatus !== "available")
}

function escapeHtml(str) {
  return String(str == null ? "" : str).replace(/[&<>"']/g, (c) => (
    { "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;", "'": "&#39;" }[c]
  ))
}

/**
 * Apply the modal copy strings (title / prompt / email labels / buttons / disclaimer).
 * Pass +full+ (the LeadCaptureModalConfig.client_payload) and +addressStatus+ to also
 * apply the how-heard dropdown rendering and per-status visibility.
 */
export function applyLeadCaptureCopyToModal($modal, copy, full, addressStatus) {
  const title = (copy.title || "").trim()
  const $header = $modal.find(".lead-capture-modal-header")
  const $titleText = $modal.find(".lead-capture-modal-title-text")
  if (title) {
    $titleText.text(title)
    $header.removeClass("d-none")
  } else {
    $titleText.text("")
    $header.addClass("d-none")
  }
  $modal.find(".lead-capture-modal-prompt").text(copy.prompt || "")
  $modal.find(".lead-capture-modal-email-label").text(copy.email_label || "")
  $modal.find(".lead-capture-modal-email").attr("placeholder", copy.email_placeholder || "")
  $modal.find(".lead-capture-modal-email-error").text(copy.email_error || "")
  $modal.find(".lead-capture-modal-cancel").text(copy.cancel_label || "")
  $modal.find(".lead-capture-modal-confirm").text(copy.submit_label || "")
  const disc = copy.disclaimer_html || ""
  $modal.find(".lead-capture-modal-disclaimer").text(disc)

  applyLeadCaptureHowHeardToModal($modal, full, addressStatus)
}

/**
 * Render the how-heard dropdown (options, label, prompt, other-keys, error copy) and
 * toggle visibility based on per-status enable. Wires the change handler that reveals
 * the free-text input for any +is_other+ option key.
 */
export function applyLeadCaptureHowHeardToModal($modal, full, addressStatus) {
  const cfg = (full && full.howHeard) || null
  const $group = $modal.find(".lead-capture-modal-how-heard-group")
  if (!cfg || !howHeardEnabledForStatus(full, addressStatus)) {
    $group.addClass("d-none")
    return
  }

  const copy = cfg.copy || {}
  $modal.find(".lead-capture-modal-how-heard-label").text(copy.label || "")

  const $select = $modal.find(".lead-capture-modal-how-heard")
  const otherKeys = []
  let html = `<option value="">${escapeHtml(copy.prompt || "")}</option>`
  for (const opt of (cfg.options || [])) {
    const k = String(opt.key == null ? "" : opt.key)
    const l = String(opt.label == null ? k : opt.label)
    if (tenantConfigBool(opt.is_other)) otherKeys.push(k)
    html += `<option value="${escapeHtml(k)}">${escapeHtml(l)}</option>`
  }
  $select.html(html)
  $select.attr("data-other-keys", JSON.stringify(otherKeys))
  $select.val("")
  $select.addClass("is-placeholder")

  const $other = $modal.find(".lead-capture-modal-how-heard-other")
  $other.attr("placeholder", copy.other_placeholder || "")
  $other.val("")
  $other.addClass("d-none")
  $other.attr("required", false)

  const $error = $modal.find(".lead-capture-modal-how-heard-error")
  $error.text(copy.error || "")
  $error.addClass("d-none")

  $select.off("change.howHeard").on("change.howHeard", () => {
    const v = String($select.val() || "")
    $select.toggleClass("is-placeholder", !v)
    const isOther = otherKeys.includes(v)
    $other.toggleClass("d-none", !isOther)
    $other.attr("required", isOther)
    if (!isOther) $other.val("")
  })

  $group.removeClass("d-none")
}

/**
 * Validate the how-heard selection and (if +is_other+) free text. Returns a result
 * object the caller can use to short-circuit submission and persist values.
 */
export function validateAndCollectHowHeard($modal, full, addressStatus) {
  if (!howHeardEnabledForStatus(full, addressStatus)) {
    return { valid: true, how_heard: "", how_heard_other: "" }
  }
  const $select = $modal.find(".lead-capture-modal-how-heard")
  const $error = $modal.find(".lead-capture-modal-how-heard-error")
  const value = String($select.val() || "").trim()
  if (!value) {
    $error.removeClass("d-none")
    return { valid: false, how_heard: "", how_heard_other: "" }
  }

  let otherKeys = []
  try {
    otherKeys = JSON.parse($select.attr("data-other-keys") || "[]")
  } catch (_e) {
    otherKeys = []
  }

  let how_heard_other = ""
  if (otherKeys.includes(value)) {
    const $other = $modal.find(".lead-capture-modal-how-heard-other")
    how_heard_other = String($other.val() || "").trim()
    if (!how_heard_other) {
      $error.removeClass("d-none")
      return { valid: false, how_heard: value, how_heard_other: "" }
    }
  }
  $error.addClass("d-none")
  return { valid: true, how_heard: value, how_heard_other }
}

/**
 * Persist the how-heard values into the surrounding form so they round-trip on submit.
 * Reads the form scope from the modal's `.lead-capture-modal-how-heard-group[data-form-scope]`
 * (set by the partial). When the scope-bound inputs already exist (status form path),
 * sets their values; otherwise injects hidden inputs into +form+ (sign-up flows).
 */
export function writeHowHeardToForm($modal, form, how_heard, how_heard_other) {
  if (!form) return
  const scope =
    $modal.find(".lead-capture-modal-how-heard-group").data("form-scope") || "accounts_sign_up"

  const setOrCreate = (name, value) => {
    let el = form.querySelector(`input[name="${name}"], select[name="${name}"]`)
    if (!el) {
      el = document.createElement("input")
      el.type = "hidden"
      el.name = name
      form.appendChild(el)
    }
    el.value = value == null ? "" : value
  }

  setOrCreate(`${scope}[how_heard]`, how_heard || "")
  setOrCreate(`${scope}[how_heard_other]`, how_heard_other || "")
}

/**
 * Opens modal; on valid email + (if applicable) how-heard, calls
 * onConfirm(email, { how_heard, how_heard_other }).
 */
export function openLeadCaptureModal({
  modalId,
  full,
  addressStatus,
  addressId,
  currentStep,
  onConfirm,
  form
}) {
  const $modal = $(modalSelector(modalId))
  if (!$modal.length) return false
  const copy = mergeLeadCaptureCopy(full, addressStatus)
  applyLeadCaptureCopyToModal($modal, copy, full, addressStatus)
  prefillLeadCaptureModalFromSession($modal, form)

  const $emailInput = $modal.find(".lead-capture-modal-email")
  if (form) {
    const hidden = form.querySelector('input[name="accounts_sign_up[email]"]')
    if (hidden && hidden.value) $emailInput.val(hidden.value)
  } else {
    $emailInput.val("")
  }

  const dismissable = leadCaptureDismissable(addressId, addressStatus, currentStep)
  $modal.find(".lead-capture-modal-cancel").toggle(dismissable)
  $modal.find(".lead-capture-modal-email-error").addClass("d-none")

  $modal.find(".lead-capture-modal-confirm").off("click.leadCapture")
  $modal.find(".lead-capture-modal-confirm").on("click.leadCapture", () => {
    const email = $modal.find(".lead-capture-modal-email").val().trim()
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailPattern.test(email)) {
      $modal.find(".lead-capture-modal-email-error").removeClass("d-none")
      return
    }
    $modal.find(".lead-capture-modal-email-error").addClass("d-none")

    const howHeard = validateAndCollectHowHeard($modal, full, addressStatus)
    if (!howHeard.valid) return

    if (form) {
      writeHowHeardToForm($modal, form, howHeard.how_heard, howHeard.how_heard_other)
    }

    $modal.modal("hide")
    saveLeadCaptureAnswers({
      email,
      how_heard: howHeard.how_heard,
      how_heard_other: howHeard.how_heard_other,
      signup_id: signupIdFromForm(form)
    })
    onConfirm(email, { how_heard: howHeard.how_heard, how_heard_other: howHeard.how_heard_other })
  })

  $modal.modal({
    backdrop: dismissable ? true : "static",
    keyboard: dismissable
  })
  $modal.modal("show")
  return true
}
